import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { CreditCard, Search, Filter, RefreshCw, Calendar, DollarSign, AlertCircle, ChevronLeft, ChevronRight, Download, FileText } from 'lucide-react';
import { paymentService } from '../services/paymentService';
import { authService } from '../services/authService';
import { downloadCSV, downloadJSON } from '../utils/exportUtils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  flexRender,
} from '@tanstack/react-table';

/**
 * Payment History Page
 * Displays all payment history with filtering, sorting, and pagination
 */
const PaymentHistory = () => {
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [payments, setPayments] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [pagination, setPagination] = useState({
    total: 0,
    page: 1,
    per_page: 20,
    total_pages: 0,
  });
  const [filters, setFilters] = useState({
    account_id: 'all',
    payment_method: 'all',
    start_date: '',
    end_date: '',
  });

  // Fetch payment history
  const fetchPayments = async (page = 1) => {
    try {
      setLoading(true);
      setError(null);
      
      const params = {
        page,
        per_page: pagination.per_page,
      };
      
      if (filters.account_id && filters.account_id !== 'all') {
        params.account_id = parseInt(filters.account_id);
      }

      if (filters.payment_method && filters.payment_method !== 'all') {
        params.payment_method = filters.payment_method;
      }

      if (filters.start_date) {
        params.start_date = filters.start_date;
      }

      if (filters.end_date) {
        params.end_date = filters.end_date;
      }
      
      const response = await paymentService.getPaymentHistory(params);
      
      setPayments(response.data?.payments || []);
      setPagination(response.data?.pagination || pagination);
    } catch (err) {
      const errorMessage = err.message || err.toString() || 'Failed to load payment history';
      setError(errorMessage);
      console.error('Payment history fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch accounts
  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const response = await authService.getAccounts();
        setAccounts(response.data?.accounts || []);
      } catch (err) {
        console.error('Error fetching accounts:', err);
      }
    };
    fetchAccounts();
  }, []);

  // Track if component has mounted to avoid double-fetching
  const hasMountedRef = useRef(false);
  const prevPathRef = useRef(null);

  // Fetch payments on mount and when filters change
  useEffect(() => {
    fetchPayments(1);
    hasMountedRef.current = true;
    prevPathRef.current = location.pathname;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.account_id, filters.payment_method, filters.start_date, filters.end_date]);

  // Refetch when navigating to this page from another route
  useEffect(() => {
    if (hasMountedRef.current && location.pathname === '/finance/payment-history' && prevPathRef.current !== '/finance/payment-history') {
      fetchPayments(pagination.page);
    }
    prevPathRef.current = location.pathname;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({
      ...prev,
      [key]: value,
    }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    fetchPayments(newPage);
  };

  const handleDownloadPayment = (payment, format) => {
    try {
      switch (format) {
        case 'csv':
          const csvData = [{
            'Invoice ID': payment.invoice_id,
            'Amount Submitted': payment.amount_submitted,
            'Payment Method': payment.payment_method,
            'Status': payment.status,
            'Approval Date': payment.approval_date || '',
            'Bank Details': payment.bank_details ? JSON.stringify(payment.bank_details) : '',
            'Transaction Details': payment.transaction_details ? JSON.stringify(payment.transaction_details) : '',
          }];
          downloadCSV(csvData, `payment-confirmation-${payment.invoice_id}`);
          break;

        case 'json':
          downloadJSON(payment, `payment-confirmation-${payment.invoice_id}`, {
            invoice_id: payment.invoice_id,
            exported_at: new Date().toISOString(),
            type: 'payment_confirmation',
          });
          break;

        default:
          break;
      }
    } catch (err) {
      const errorMessage = err.message || err.toString() || 'Failed to download payment confirmation';
      alert(errorMessage);
      console.error('Download error:', err);
    }
  };

  // Format currency
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount || 0);
  };

  // Get payment method badge
  const getPaymentMethodBadge = (method) => {
    const methodConfig = {
      'PayPal': { variant: 'default', label: 'PayPal' },
      'Wire Transfer': { variant: 'secondary', label: 'Wire Transfer' },
      'eCheck': { variant: 'outline', label: 'eCheck' },
    };

    const config = methodConfig[method] || { variant: 'outline', label: method };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  // Get status badge
  const getStatusBadge = (status) => {
    const statusConfig = {
      'completed': { variant: 'default', label: 'Completed' },
      'pending': { variant: 'secondary', label: 'Pending' },
      'failed': { variant: 'destructive', label: 'Failed' },
    };

    const config = statusConfig[status] || { variant: 'outline', label: status };
    return <Badge variant={config.variant}>{config.label}</Badge>;
  };

  // Table columns
  const columns = useMemo(() => [
    {
      accessorKey: 'invoice_id',
      header: 'Invoice ID',
      cell: ({ row }) => {
        const payment = row.original;
        return (
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-muted flex items-center justify-center">
              <FileText className="w-5 h-5" />
            </div>
            <div>
              <div className="font-medium">{payment.invoice_id}</div>
              {payment.approval_date && (
                <div className="text-sm text-muted-foreground">
                  Approved: {new Date(payment.approval_date).toLocaleDateString()}
                </div>
              )}
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: 'amount_submitted',
      header: 'Amount',
      cell: ({ getValue }) => {
        const amount = getValue();
        return (
          <div className="flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-muted-foreground" />
            <span className="font-medium">{formatCurrency(amount)}</span>
          </div>
        );
      },
    },
    {
      accessorKey: 'payment_method',
      header: 'Payment Method',
      cell: ({ getValue }) => {
        const method = getValue();
        return getPaymentMethodBadge(method);
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ getValue }) => {
        const status = getValue();
        return getStatusBadge(status);
      },
    },
    {
      accessorKey: 'approval_date',
      header: 'Approval Date',
      cell: ({ getValue }) => {
        const date = getValue();
        if (!date) return '-';
        return (
          <div className="flex items-center gap-2 text-sm">
            <Calendar className="w-4 h-4 text-muted-foreground" />
            {new Date(date).toLocaleDateString()}
          </div>
        );
      },
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => {
        const payment = row.original;
        
        return (
          <div className="flex items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">
                  <Download className="w-4 h-4 mr-1" />
                  Download
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={() => handleDownloadPayment(payment, 'csv')}>
                  <FileText className="w-4 h-4 mr-2" />
                  Download as CSV
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleDownloadPayment(payment, 'json')}>
                  <FileText className="w-4 h-4 mr-2" />
                  Download as JSON
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        );
      },
    },
  ], []);

  const table = useReactTable({
    data: payments,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
    pageCount: pagination.total_pages,
    state: {
      pagination: {
        pageIndex: pagination.page - 1,
        pageSize: pagination.per_page,
      },
    },
  });

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 overflow-x-hidden">
      <div className="mb-2 sm:mb-3 lg:mb-4">
        <div>
          <h1 className="text-lg sm:text-xl lg:text-2xl font-bold mb-0.5">Payment History</h1>
          <p className="text-muted-foreground">View your payment history and transactions</p>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-2 sm:mb-3 lg:mb-4">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Filter className="h-5 w-5" />
            Filters
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1.5">Account</label>
              <Select
                value={filters.account_id}
                onValueChange={(value) => handleFilterChange('account_id', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All accounts" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All accounts</SelectItem>
                  {accounts.map(account => (
                    <SelectItem key={account.id} value={account.id.toString()}>
                      {account.account_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">Payment Method</label>
              <Select
                value={filters.payment_method}
                onValueChange={(value) => handleFilterChange('payment_method', value)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="All methods" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All methods</SelectItem>
                  <SelectItem value="PayPal">PayPal</SelectItem>
                  <SelectItem value="Wire Transfer">Wire Transfer</SelectItem>
                  <SelectItem value="eCheck">eCheck</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">Start Date</label>
              <Input
                type="date"
                value={filters.start_date}
                onChange={(e) => handleFilterChange('start_date', e.target.value)}
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">End Date</label>
              <Input
                type="date"
                value={filters.end_date}
                onChange={(e) => handleFilterChange('end_date', e.target.value)}
              />
            </div>
            <div className="flex items-end">
              <Button
                variant="outline"
                onClick={() => fetchPayments(pagination.page)}
                className="w-full"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Error message */}
      {error && (
        <Card className="mb-2 sm:mb-3 lg:mb-4 border-destructive">
          <CardContent className="pt-6">
            <div className="flex items-center gap-2 text-destructive">
              <AlertCircle className="w-5 h-5" />
              <span>{error}</span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Payments table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-muted-foreground">Loading payment history...</div>
          ) : payments.length === 0 ? (
            <div className="p-8 text-center">
              <CreditCard className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
              <p className="text-lg font-medium mb-2">No payment history found</p>
              <p className="text-muted-foreground">Payment history will appear here once payments are processed</p>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    {table.getHeaderGroups().map(headerGroup => (
                      <tr key={headerGroup.id} className="border-b">
                        {headerGroup.headers.map(header => (
                          <th
                            key={header.id}
                            className="h-12 px-2 sm:px-4 text-left align-middle font-medium text-muted-foreground text-xs sm:text-sm"
                          >
                            {header.isPlaceholder
                              ? null
                              : flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody>
                    {table.getRowModel().rows.map(row => (
                      <tr
                        key={row.id}
                        className="border-b transition-colors hover:bg-muted/50"
                      >
                        {row.getVisibleCells().map(cell => (
                          <td
                            key={cell.id}
                            className="p-2 sm:p-4 align-middle text-sm"
                          >
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination.total_pages > 1 && (
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 border-t gap-4">
                  <div className="text-sm text-muted-foreground text-center sm:text-left">
                    Showing {((pagination.page - 1) * pagination.per_page) + 1} to{' '}
                    {Math.min(pagination.page * pagination.per_page, pagination.total)} of{' '}
                    {pagination.total} payments
                  </div>
                  <div className="flex items-center justify-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={pagination.page === 1}
                    >
                      <ChevronLeft className="w-4 h-4" />
                      Previous
                    </Button>
                    <div className="text-sm">
                      Page {pagination.page} of {pagination.total_pages}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={pagination.page >= pagination.total_pages}
                    >
                      Next
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default PaymentHistory;
